import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";
import { baseURL, requestURLs } from "@/Environment";
import i18n from "@/i18n/config";
// import { toast } from "react-toastify";
const ApiRequest = axios.create({
  baseURL,
});

// In-flight mutation promises map: key -> Promise<AxiosResponse>
const inFlightMutations = new Map<string, Promise<any>>();

const getMutationDedupeKey = (
  method?: string,
  url?: string,
  data?: unknown,
  params?: unknown
): string | null => {
  const m = (method || "get").toLowerCase();
  if (!["post", "put", "patch", "delete"].includes(m)) {
    return null;
  }
  // Exclude refresh and access token routes from deduplication
  if (url?.includes("/auth/access-token") || url?.includes("/auth/refresh-token")) {
    return null;
  }
  // If data is FormData, skip stringifying
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    return null;
  }
  try {
    const dataStr = data ? JSON.stringify(data) : "";
    const paramsStr = params ? JSON.stringify(params) : "";
    return `${m}:${url || ""}:${paramsStr}:${dataStr}`;
  } catch {
    return null;
  }
};

const origPost = ApiRequest.post.bind(ApiRequest);
const origPut = ApiRequest.put.bind(ApiRequest);
const origPatch = ApiRequest.patch.bind(ApiRequest);
const origDelete = ApiRequest.delete.bind(ApiRequest);

ApiRequest.post = function <T = any, R = AxiosResponse<T>, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig<D>
): Promise<R> {
  const key = getMutationDedupeKey("post", url, data, config?.params);
  if (key && inFlightMutations.has(key)) {
    return inFlightMutations.get(key)!;
  }
  const promise = origPost<T, R, D>(url, data, config)
    .then((res) => {
      setTimeout(() => {
        if (key) inFlightMutations.delete(key);
      }, 1000);
      return res;
    })
    .catch((err) => {
      if (key) inFlightMutations.delete(key);
      return Promise.reject(err);
    });
  if (key) inFlightMutations.set(key, promise);
  return promise;
};

ApiRequest.put = function <T = any, R = AxiosResponse<T>, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig<D>
): Promise<R> {
  const key = getMutationDedupeKey("put", url, data, config?.params);
  if (key && inFlightMutations.has(key)) {
    return inFlightMutations.get(key)!;
  }
  const promise = origPut<T, R, D>(url, data, config)
    .then((res) => {
      setTimeout(() => {
        if (key) inFlightMutations.delete(key);
      }, 1000);
      return res;
    })
    .catch((err) => {
      if (key) inFlightMutations.delete(key);
      return Promise.reject(err);
    });
  if (key) inFlightMutations.set(key, promise);
  return promise;
};

ApiRequest.patch = function <T = any, R = AxiosResponse<T>, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig<D>
): Promise<R> {
  const key = getMutationDedupeKey("patch", url, data, config?.params);
  if (key && inFlightMutations.has(key)) {
    return inFlightMutations.get(key)!;
  }
  const promise = origPatch<T, R, D>(url, data, config)
    .then((res) => {
      setTimeout(() => {
        if (key) inFlightMutations.delete(key);
      }, 1000);
      return res;
    })
    .catch((err) => {
      if (key) inFlightMutations.delete(key);
      return Promise.reject(err);
    });
  if (key) inFlightMutations.set(key, promise);
  return promise;
};

ApiRequest.delete = function <T = any, R = AxiosResponse<T>, D = any>(
  url: string,
  config?: AxiosRequestConfig<D>
): Promise<R> {
  const key = getMutationDedupeKey("delete", url, config?.data, config?.params);
  if (key && inFlightMutations.has(key)) {
    return inFlightMutations.get(key)!;
  }
  const promise = origDelete<T, R, D>(url, config)
    .then((res) => {
      setTimeout(() => {
        if (key) inFlightMutations.delete(key);
      }, 1000);
      return res;
    })
    .catch((err) => {
      if (key) inFlightMutations.delete(key);
      return Promise.reject(err);
    });
  if (key) inFlightMutations.set(key, promise);
  return promise;
};
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

ApiRequest.interceptors.request.use((config) => {
  config.headers["organisation-id"] = localStorage.getItem("organisation-id");
  config.headers["Accept-Language"] = i18n.language || "en";

  if (window.location.pathname.includes("collaborator")) {
    config.headers["user-id"] = window.location.pathname.split("/")[2];
  }
  config.withCredentials = true;
  return config;
});

ApiRequest.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const { pathname } = window.location;
    const isOnLoginPage = pathname === "/login";
    const isOnSignupPage = pathname === "/signup";
    const isOnForgotPasswordPage = pathname === "/forgot-password";
    const isOnResetPasswordPage = pathname.includes("/reset-password");
    const isCollaboratorPage = pathname.includes("collaborator");
    if (isOnLoginPage || isOnSignupPage || isOnForgotPasswordPage || isOnResetPasswordPage || isCollaboratorPage) {
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes("/auth/access-token")) {
        // toast.error("Session expired, please login again");
        return Promise.reject(error);
      }
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return ApiRequest(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await ApiRequest.get(requestURLs.refreshToken);
        isRefreshing = false;
        processQueue();
        return ApiRequest(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError);
        // toast.error("Session expired, please login again");
        // window.location.pathname = "/login";
        return Promise.reject(refreshError);
      }
    }
    if (error.response?.data?.errors && Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {
      const firstError = error.response.data.errors[0];
      if (firstError && typeof firstError.message === "string") {
        error.response.data.message = firstError.message;
      }
    }
    return Promise.reject(error);
  }
);

export default ApiRequest;
